import React, { useCallback, useMemo, useState } from 'react';

import MainContainer from 'psims/react/pages/primary-pages/data-submissions/shared/main-container';
import { UseRefinerAnnualActivityAPI } from './use-refiner-annual-activity-api';
import useRefinerAnnualActivity from './use-refiner-annual-activity';
import useSubmissionProcessingOverlay from '../../shared/use-submission-processing-overlay';
import { BoxedDiv } from 'psims/react/components/layout';
import Header from 'psims/react/blocks/data-submission-header';
import { assertDataSubmission } from 'psims/models/data-submission';
import StorageSitesEditor from '../shared/storage-sites/storage-sites-editor';
import RefinerAnnualActivitiesEditor from './annual-activities/refiner-annual-activities-editor';
import ConfirmationDialog from 'psims/react/components/confirmation-dialog';
import Notifications from '../../shared/notifications';
import ScrollTo from 'psims/react/components/scroll-to';
import SubmissionProgressBar from 'psims/react/components/submission-progress-bar';
import { H1, H2 } from 'psims/react/components/typography';
import SubmitPage from '../../shared/submit-page';
import DataSubmissionPrimaryControls from 'psims/react/components/data-submission-primary-controls';
import Button from 'psims/react/components/button';
import ConfirmSaveChanges from 'psims/react/components/confirm-save-changes';
import { getSubmissionTypeName } from 'psims/constants/data-submissions';
import MsoCommentEditor from '../shared/mso-comment/mso-comment-editor';
import ImporterImportsEditor from '../shared/imports/importer-imports-editor';
import MsoStockholdingStoragesEditor from '../shared/mso-stockholding-storages/mso-stockholding-storages-editor';
import EnforceableArrangementsEditor from '../shared/enforceable-arrangements/enforceable-arrangements-editor';
import AustralianControllingCorporationsEditor from '../shared/australian-controlling-corporations/australian-controlling-corporations-editor';
import styled from 'styled-components';
import RefinerProductionEditor from '../shared/production/refiner-production-editor';
//import RefinerProductionEditor from '../shared/production/refiner-production-editor';


interface RefinerAnnualActivityEditorProps {
  apiCtrl: PopulatedProps<UseRefinerAnnualActivityAPI, 'submission'>;
}
 const InfoBox = styled.div`
  background: #f5f7fa;
  border: 1px solid #d6dde6;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
  color: #333;
  width:auto;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
  }
`;
const RefinerAnnualActivityEditor = (props: RefinerAnnualActivityEditorProps) => {
  const vm = useVM(props);
 
  const {primaryControl, secondaryControl} = vm.controlsCtrl;

  return <MainContainer dataSubmission={vm.dataSubmission}>
    <BoxedDiv box={{}}>
      <Header
        customName={getSubmissionTypeName(vm.dataSubmission)}
        dataSubmission={vm.dataSubmission}
        showExtraButtons={vm.shouldShowExtraButtons}
        handleClearData={vm.clearDataCtrl.handleClearData}
        currentStepIndex={vm.progressCtrl.currentStep.index}
      />
    </BoxedDiv>

    {/* Progress indicator */}
    <BoxedDiv box={{ marginV: 6 }}>
      <SubmissionProgressBar
        isComplete={vm.viewMode === 'edit'}
        steps={vm.progressCtrl.progressSteps}
        onStepClick={vm.navCtrl.attemptStepNavigation}
      />
    </BoxedDiv>

    <div className='container'>
      {/* Switch on current page */}
      {
        vm.progressCtrl.currentStep.index === 0 && <>
        {<BoxedDiv box={{ marginTop: 2, flex:'column',alignItems:'flex-start', alignSelf:'center' }}>
            <H2>Imports</H2>
          </BoxedDiv>}
          <InfoBox>
          <BoxedDiv box={{flex: 'column'}}>
            <p>
              This page provides the information your entity is required to fill out under 19F(2) of the <i>Petroleum and Other Fuels Reporting (POFR) Rules 2017</i>.
            </p>
          </BoxedDiv>
          </InfoBox>
          {/*<BoxedDiv box={{ marginTop: 2, flex:'column',alignItems:'center', alignSelf:'center' }}>
            <H1>Imports</H1>
          </BoxedDiv>*/}

          <ImporterImportsEditor
            dataSubmissionId={vm.apiCtrl.submission.dataSubmission.id}
            deleteCtrl={vm.deleteCtrl}
            focusFieldCtrl={vm.focusFieldCtrl}
            forceErrors={vm.navCtrl.lastSaveAttemptIndex != null}
            importerImportsCtrl={vm.importsCtrl}
            isDisabled={vm.isDisabled}
            isOnBehalf={true}
            products={vm.refData.products}
          />

          <ImporterImportsEditor
            dataSubmissionId={vm.apiCtrl.submission.dataSubmission.id}
            deleteCtrl={vm.deleteCtrl}
            focusFieldCtrl={vm.focusFieldCtrl}
            forceErrors={vm.navCtrl.lastSaveAttemptIndex != null}
            importerImportsCtrl={vm.importsCtrl}
            isDisabled={vm.isDisabled}
            isOnBehalf={false}
            products={vm.refData.products}
          />

          <RefinerAnnualActivitiesEditor
            annualActivitiesCtrl={vm.annualActivitiesCtrl}
            deleteCtrl={vm.deleteCtrl}
            focusFieldCtrl={vm.focusFieldCtrl}
            forceErrors={vm.navCtrl.lastSaveAttemptIndex != null}
            isDisabled={vm.isDisabled}
            products={vm.refData.products} 
            msoTargetDays={vm.msoTargetDays}            
          />
     {/* Comments */}
          <ImportsCommentEditor
              isDisabled={vm.isDisabled}
              msoCommentAnnualActivities={vm.apiCtrl.submission.msoCommentAnnualActivities ?? []}
              onCommentChange={vm.handleImportsCommentChange}
          />
        </>
      }

        {
        vm.progressCtrl.currentStep.index === 1 && <>
          <BoxedDiv box={{ marginTop: 2 }}>
            <H2>Production</H2>
          </BoxedDiv>
            <InfoBox>
          <p style={{ marginRight: 0 }}>This section requires you to fill in the aggregate volumes for refining each MSO product in 2023. Please note that the refining volumes reported under this section must be consistent with the reports required under the <i>Petroleum and Other Fuels Reporting Act</i>.  Quantities should be reported to the nearest megalitre (ML).</p>
        </InfoBox>
       
          <RefinerProductionEditor
            focusFieldCtrl={vm.focusFieldCtrl}
            forceErrors={vm.navCtrl.lastSaveAttemptIndex != null}
            isDisabled={vm.isDisabled}
            products={vm.refData.products}
            productionCtrl={vm.productionCtrl}
         />
         {/* Comments */}
          <ProductionCommentEditor
    isDisabled={vm.isDisabled}
    msoCommentAnnualActivities={vm.apiCtrl.submission.msoCommentAnnualActivities ?? []}
    onCommentChange={vm.handleProductionCommentChange}
/>
        </>
      }


      {
        vm.progressCtrl.currentStep.index === 2 &&
        <>
        {/*  <BoxedDiv box={{ marginTop: 2, flex:'column',alignItems:'center', alignSelf:'center' }}>
            <H1>Storage sites</H1>
          </BoxedDiv>
        */}
         <MsoStockholdingStoragesEditor
            caseId={vm.apiCtrl.submission.dataSubmission.caseId}
            deleteCtrl={vm.deleteCtrl}
            products={vm.refData.products}
            focusFieldCtrl={vm.focusFieldCtrl}
            isDisabled={vm.isDisabled}
            storageSitesCtrl={vm.msoStockholdingStorageCtrl}
            storageTypes={vm.refData.storageTypes}
            isRefiner={false}
            forceErrors={vm.navCtrl.lastSaveAttemptIndex ===2}
            msoCommentAnnualActivities={
                vm.apiCtrl.submission.msoCommentAnnualActivities ?? []
            }
              onCommentChange={vm.handleCommentChange}
          />
          
        </>
      }
      {
        vm.progressCtrl.currentStep.index === 3 &&
        <>
        <EnforceableArrangementsEditor
            caseId={vm.apiCtrl.submission.dataSubmission.caseId}
            ctrl={vm.enforceableArrangementsCtrl}
            products={vm.refData.products}
            forceErrors={vm.navCtrl.lastSaveAttemptIndex === 3}
            isDisabled={vm.isDisabled}
            focusFieldCtrl={vm.focusFieldCtrl}
            deleteCtrl={vm.deleteCtrl}
            msoCommentAnnualActivities={vm.apiCtrl.submission.msoCommentAnnualActivities ?? []}
            onCommentChange={vm.handleEnforceableArrangementsCommentChange}
        />
        </>
      }
      {
        vm.progressCtrl.currentStep.index === 4 &&
        <>
         {/* <BoxedDiv box={{ marginTop: 2 }}>
            <H2>Australian Controlling Corporation</H2>
          </BoxedDiv>*/}
      
      <AustralianControllingCorporationsEditor
        ctrl={vm.australianControllingCorporationsCtrl}
        isDisabled={vm.isDisabled}
        msoCommentAnnualActivities={vm.apiCtrl.submission.msoCommentAnnualActivities ?? []}
        onCommentChange={vm.handleAustralianControllingCorpsCommentChange}
        reportingYear={vm.apiCtrl.submission.dataSubmission.reportingYear }
    />

        </>
      }


      {
        vm.progressCtrl.currentStep.index === 5 &&
        <>
          <BoxedDiv box={{ marginTop: 2 }}>
            <H2>Declaration</H2>
          </BoxedDiv>

          <SubmitPage
            ctrl={{
              ...vm.submitCtrl,
              isDisabled: vm.isDisabled,
              commentsError: undefined,
              dataSubmission: vm.apiCtrl.submission.dataSubmission,
            }}
            focusedFieldCtrl={vm.focusFieldCtrl}
            isMso={true}
            viewMode={vm.viewMode}
          />
        </>
      }


      {/* Notifications */}
      {
        !vm.isDisabled &&
        <Notifications
          infoNotifications={vm.notificationsCtrl.infoNotifications}
          systemNotifications={vm.notificationsCtrl.serviceNotifications}
          validationNotifications={vm.notificationsCtrl.validationNotifications}
        />
      }

      {/* Controls */}
      <DataSubmissionPrimaryControls>
          <BoxedDiv box={{alignItems: 'center', flex: 'row-reverse', marginTop: 2}}>
              {
                  primaryControl.isShown &&
                  <Button
                      disabled={primaryControl.isDisabled}
                      $kind='primary'
                      marginLeft={2}
                      onClick={primaryControl.action}
                      state={primaryControl.state}
                  >{primaryControl.label}</Button>
              }

              {
                  secondaryControl.isShown &&
                  <Button
                      disabled={secondaryControl.isDisabled}
                      $kind='ghost'
                      onClick={secondaryControl.action}
                      state={secondaryControl.state}
                  >{secondaryControl.label}</Button>
              }
          </BoxedDiv>
      </DataSubmissionPrimaryControls>

    </div>

    {/* Confirm Clear all dialog */}
    {
        vm.progressCtrl.currentStep.index === 0 &&
        <ConfirmationDialog
            ariaLabel='Clear all data confirm'
            body={vm.clearDataCtrl.confirmCtlr.message}
            title={vm.clearDataCtrl.confirmCtlr.title}
            onCancel={vm.clearDataCtrl.confirmCtlr.cancel}
            onConfirm={vm.clearDataCtrl.confirmCtlr.confirm}
            isOpen={vm.clearDataCtrl.confirmCtlr.confirmState === 'confirming'}
        />
    }

    <ConfirmationDialog
      ariaLabel='Record delete confirmation'
      isOpen={vm.deleteCtrl.confirmState === 'confirming'}
      body={vm.deleteCtrl.message}
      onCancel={vm.deleteCtrl.cancel}
      onConfirm={vm.deleteCtrl.confirm}
      title={vm.deleteCtrl.title}
      confirmLabel='Yes'
      cancelLabel='No'
    />
    
    <ConfirmSaveChanges {...vm.navCtrl.unsavedChangesDialogCtrl} />

    {/* Scroll helper */}
    <ScrollTo />
  </MainContainer>
}

function useVM({ apiCtrl }: RefinerAnnualActivityEditorProps) {
  const ctrl = useRefinerAnnualActivity({ apiCtrl });

  useSubmissionProcessingOverlay({ submissionStatus: apiCtrl.loadStatus });

  const dataSubmission = useMemo(() => {
    const { dataSubmission } = apiCtrl.submission;
    assertDataSubmission(dataSubmission, 'MSO importer');
    
    return dataSubmission
  }, [apiCtrl.submission]);
  
  const shouldShowExtraButtons = useMemo(() => (
    ctrl.progressCtrl.currentStep.index === 0 && ctrl.viewMode === 'edit'),
    [ctrl.progressCtrl.currentStep.index, ctrl.viewMode]
  );

  return {
    ...ctrl,
    dataSubmission,
    shouldShowExtraButtons,
     msoTargetDays: apiCtrl.submission.msoTargetDays ?? [],
  };
}
// ─── Comment editor components ────────────────────────────────────────────────

interface PageCommentEditorProps {
    isDisabled: boolean;
    msoCommentAnnualActivities: Array<{ commentCategoryType?: number | null; reporterComments?: string }>;
    onCommentChange: (comment: string) => void;
}

const CommentTextarea = ({ id, label, comment, isDisabled, onChange }: {
    id: string;
    label: string;
    comment: string;
    isDisabled: boolean;
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}) => (
    <BoxedDiv box={{ marginTop: 4 }}>
        <label htmlFor={id} style={{ display: 'block', fontWeight: 'bold', marginBottom: 8 }}>{label}</label>
        <textarea
            id={id}
            disabled={isDisabled}
            value={comment}
            onChange={onChange}
            maxLength={4000}
            rows={4}
            style={{ width: '100%', padding: '8px', fontSize: '14px', border: '1px solid #ccc', borderRadius: '4px' }}
        />
        <div style={{ textAlign: 'right', fontSize: '13px', color: '#666' }}>
            {comment.length} of 4000 characters used
        </div>
    </BoxedDiv>
);

const ImportsCommentEditor = ({ isDisabled, msoCommentAnnualActivities, onCommentChange }: PageCommentEditorProps) => {
    const initialComment = useMemo(() =>
        msoCommentAnnualActivities.find(c => c.commentCategoryType === 1)?.reporterComments ?? '',
        [msoCommentAnnualActivities]
    );
    const [comment, setComment] = useState(initialComment);
    const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setComment(e.target.value);
        onCommentChange(e.target.value);
    }, [onCommentChange]);
    return <CommentTextarea id="imports_comment" label="Comments" comment={comment} isDisabled={isDisabled} onChange={handleChange} />;
};

const ProductionCommentEditor = ({ isDisabled, msoCommentAnnualActivities, onCommentChange }: PageCommentEditorProps) => {
    const initialComment = useMemo(() =>
        msoCommentAnnualActivities.find(c => c.commentCategoryType === 2)?.reporterComments ?? '',
        [msoCommentAnnualActivities]
    );
    const [comment, setComment] = useState(initialComment);
    const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setComment(e.target.value);
        onCommentChange(e.target.value);
    }, [onCommentChange]);
    return <CommentTextarea id="production_comment" label="Comments" comment={comment} isDisabled={isDisabled} onChange={handleChange} />;
};
export default RefinerAnnualActivityEditor;
